import { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { getMyTenant, patchMyTenant } from "@/api/tenants";
import { usePermission } from "@/auth/usePermission";
import { PageHeader } from "@/components/PageHeader";
import { useAuthStore } from "@/store/authStore";

const LOCALES = ["fr", "en"];

export function TenantSettingsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canEdit = usePermission("tenant:update");
  // GOD accounts carry no tenant_id, so /me/tenant answers 400. Skip the call
  // and point them to the platform-wide tenant list instead.
  const hasTenant = Boolean(useAuthStore((s) => s.me)?.tenant_id);

  const tenant = useQuery({
    queryKey: ["me-tenant"],
    queryFn: getMyTenant,
    enabled: hasTenant,
  });

  const [name, setName] = useState("");
  const [brandColor, setBrandColor] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [defaultLocale, setDefaultLocale] = useState("fr");
  const [auditRetentionDays, setAuditRetentionDays] = useState(730);
  const [planBlancEnabled, setPlanBlancEnabled] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (tenant.data) {
      setName(tenant.data.name);
      setBrandColor(tenant.data.brand_color ?? "");
      setSupportEmail(tenant.data.support_email ?? "");
      setDefaultLocale(tenant.data.default_locale ?? "fr");
      setAuditRetentionDays(tenant.data.audit_retention_days ?? 730);
      setPlanBlancEnabled(tenant.data.plan_blanc_enabled ?? false);
    }
  }, [tenant.data]);

  const save = useMutation({
    mutationFn: () =>
      patchMyTenant({
        name: name || undefined,
        brand_color: brandColor || null,
        support_email: supportEmail || null,
        default_locale: defaultLocale || undefined,
        audit_retention_days: auditRetentionDays,
        plan_blanc_enabled: planBlancEnabled,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me-tenant"] });
      setSaved(true);
      setServerError(null);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (e: unknown) => {
      const d = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setServerError(d ?? (e as Error).message);
    },
  });

  if (!hasTenant) {
    return (
      <Box>
        <PageHeader title={t("tenantSettings.title")} helpTerm="tenant" />
        <Alert severity="info">
          {t("tenantSettings.noTenantContext")}{" "}
          <Link component={RouterLink} to="/admin/tenants">
            {t("nav.tenants")}
          </Link>
        </Alert>
      </Box>
    );
  }
  if (tenant.isLoading) return <CircularProgress />;
  if (tenant.error) {
    const detail = (tenant.error as { response?: { data?: { detail?: string } } }).response?.data
      ?.detail;
    return (
      <Box>
        <PageHeader title={t("tenantSettings.title")} helpTerm="tenant" />
        <Alert severity="error">{detail ?? (tenant.error as Error).message}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t("tenantSettings.title")}
        subtitle={t("tenantSettings.subtitle")}
      />

      <Card variant="outlined" sx={{ maxWidth: 720 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="overline" color="text.secondary">
                {t("tenantSettings.slug")}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {tenant.data?.slug}
              </Typography>
              <Chip
                size="small"
                color={tenant.data?.status === "ACTIVE" ? "success" : "default"}
                label={tenant.data?.status}
              />
              <Chip size="small" variant="outlined" label={tenant.data?.plan} />
            </Stack>

            <TextField
              label={t("tenantSettings.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              disabled={!canEdit}
              fullWidth
            />
            <TextField
              label={t("tenantSettings.brandColor")}
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
              placeholder="#2563FF"
              size="small"
              disabled={!canEdit}
              helperText={t("tenantSettings.brandColorHelp")}
              InputProps={{
                endAdornment: brandColor && (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: brandColor,
                      borderRadius: 0.5,
                      ml: 1,
                      border: 1,
                      borderColor: "divider",
                    }}
                  />
                ),
              }}
            />
            <TextField
              label={t("tenantSettings.supportEmail")}
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
              size="small"
              type="email"
              disabled={!canEdit}
            />
            <FormControl size="small">
              <InputLabel>{t("tenantSettings.defaultLocale")}</InputLabel>
              <Select
                label={t("tenantSettings.defaultLocale")}
                value={defaultLocale}
                onChange={(e) => setDefaultLocale(e.target.value)}
                disabled={!canEdit}
              >
                {LOCALES.map((l) => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t("tenantSettings.auditRetentionDays")}
              type="number"
              size="small"
              value={auditRetentionDays}
              onChange={(e) => setAuditRetentionDays(Number(e.target.value) || 0)}
              inputProps={{ min: 30, max: 3650 }}
              disabled={!canEdit}
              helperText={t("tenantSettings.auditRetentionHelp")}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={planBlancEnabled}
                  onChange={(e) => setPlanBlancEnabled(e.target.checked)}
                  disabled={!canEdit}
                />
              }
              label={t("tenantSettings.planBlancEnabled")}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              {t("tenantSettings.planBlancEnabledHelp")}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                {t("tenantSettings.counts", {
                  orgs: tenant.data?.org_count ?? 0,
                  users: tenant.data?.user_count ?? 0,
                  practitioners: tenant.data?.practitioner_count ?? 0,
                })}
              </Typography>
              {canEdit && (
                <Button
                  variant="contained"
                  onClick={() => save.mutate()}
                  disabled={save.isPending}
                >
                  {t("common.save")}
                </Button>
              )}
            </Stack>

            {serverError && <Alert severity="error">{serverError}</Alert>}
            {saved && <Alert severity="success">{t("tenantSettings.saved")}</Alert>}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
